/**
 * Playback Store - Timeline and Playhead State Management
 *
 * This store manages the playback state:
 * - Current playhead position (time)
 * - Play/pause state
 * - Playback timing calculations
 *
 * The playback loop uses requestAnimationFrame for smooth animation.
 * When playing, it updates the currentTime based on real elapsed time.
 *
 * HOW PLAYBACK WORKS:
 * ===================
 * 1. User clicks play -> isPlaying = true
 * 2. startPlayback() records the start time and begins the animation loop
 * 3. Each frame, we calculate: currentTime = startTime + (now - playStartedAt)
 * 4. Components subscribe to currentTime and update accordingly
 * 5. User clicks pause -> isPlaying = false, loop stops
 */

import { create } from 'zustand';

// =============================================================================
// STORE INTERFACE
// =============================================================================

interface PlaybackStore {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  /** Whether the timeline is currently playing */
  isPlaying: boolean;

  /** Current playhead position in seconds */
  currentTime: number;

  /** Total duration (calculated from project nodes) */
  totalDuration: number;

  /** ID of the beat node currently at the playhead position */
  focusedNodeId: string | null;

  /** Playback direction: 1 for forward, -1 for reverse */
  playbackDirection: number;

  /** Playback speed multiplier: 1 = normal, 2 = 2x, 3 = 3x, 4 = 4x */
  playbackSpeed: number;

  /** Master volume for all audio (0-1) */
  masterVolume: number;

  // Internal state for the animation loop
  /** Timestamp when playback started (for calculating elapsed time) */
  _playStartedAt: number | null;
  /** What currentTime was when play was pressed */
  _startTime: number;
  /** Reference to the animation frame for cleanup */
  _animationFrameId: number | null;

  // ---------------------------------------------------------------------------
  // ACTIONS
  // ---------------------------------------------------------------------------

  /**
   * Start or resume playback
   */
  play: () => void;

  /**
   * Play forward (sets direction to forward and starts playback)
   */
  playForward: () => void;

  /**
   * Play in reverse (sets direction to reverse and starts playback)
   */
  playReverse: () => void;

  /**
   * Pause playback
   */
  pause: () => void;

  /**
   * Stop playback (same as pause, for K key)
   */
  stop: () => void;

  /**
   * Toggle between play and pause
   */
  togglePlayback: () => void;

  /**
   * Set the playhead to a specific time
   * @param time - Time in seconds
   */
  seekTo: (time: number) => void;

  /**
   * Jump to the start of the timeline
   */
  goToStart: () => void;

  /**
   * Jump to the end of the timeline
   */
  goToEnd: () => void;

  /**
   * Update the total duration (called when nodes change)
   * @param duration - Total duration in seconds
   */
  setTotalDuration: (duration: number) => void;

  /**
   * Update the focused node ID (called when timeline position changes)
   * @param nodeId - ID of the node at current playhead position
   */
  setFocusedNodeId: (nodeId: string | null) => void;

  /**
   * Increase playback speed (for multiple J/L key presses)
   * Cycles through 1x, 2x, 3x, 4x speeds
   */
  increaseSpeed: () => void;

  /**
   * Reset playback speed to 1x
   */
  resetSpeed: () => void;

  /**
   * Set master volume (0-1)
   */
  setMasterVolume: (volume: number) => void;

  /**
   * Internal: Called by the animation loop to update currentTime
   */
  _tick: () => void;

  /**
   * Cleanup the animation loop (call on unmount)
   */
  cleanup: () => void;
}

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const usePlaybackStore = create<PlaybackStore>((set, get) => ({
  // ---------------------------------------------------------------------------
  // INITIAL STATE
  // ---------------------------------------------------------------------------

  isPlaying: false,
  currentTime: 0,
  totalDuration: 0,
  focusedNodeId: null,
  playbackDirection: 1, // 1 for forward, -1 for reverse
  playbackSpeed: 1, // 1x speed by default
  masterVolume: 1, // 100% volume by default

  _playStartedAt: null,
  _startTime: 0,
  _animationFrameId: null,

  // ---------------------------------------------------------------------------
  // ACTIONS
  // ---------------------------------------------------------------------------

  play: () => {
    const state = get();

    // Don't play if already playing or no duration
    if (state.isPlaying) return;
    if (state.totalDuration === 0) return;

    // If we're at the end (forward) or start (reverse), reset to opposite end
    let startTime = state.currentTime;
    if (state.playbackDirection === 1 && startTime >= state.totalDuration) {
      startTime = 0;
    } else if (state.playbackDirection === -1 && startTime <= 0) {
      startTime = state.totalDuration;
    }

    set({
      isPlaying: true,
      _playStartedAt: performance.now(),
      _startTime: startTime,
    });

    // Start the animation loop
    const tick = () => {
      const currentState = get();
      if (!currentState.isPlaying) return;

      currentState._tick();

      // Continue the loop
      const frameId = requestAnimationFrame(tick);
      set({ _animationFrameId: frameId });
    };

    const frameId = requestAnimationFrame(tick);
    set({ _animationFrameId: frameId });
  },

  playForward: () => {
    const state = get();

    // If already playing forward, increase speed (for repeated L key presses)
    if (state.isPlaying && state.playbackDirection === 1) {
      state.increaseSpeed();
      return;
    }

    // If playing in reverse or paused, start forward playback
    if (state.isPlaying && state.playbackDirection !== 1) {
      set({
        playbackDirection: 1,
        playbackSpeed: 1, // Reset speed when changing direction
        _playStartedAt: performance.now(),
        _startTime: state.currentTime,
      });
    } else {
      set({ playbackDirection: 1, playbackSpeed: 1 });
      state.play();
    }
  },

  playReverse: () => {
    const state = get();

    // If already playing in reverse, increase speed (for repeated J key presses)
    if (state.isPlaying && state.playbackDirection === -1) {
      state.increaseSpeed();
      return;
    }

    // If playing forward or paused, start reverse playback
    if (state.isPlaying && state.playbackDirection !== -1) {
      set({
        playbackDirection: -1,
        playbackSpeed: 1, // Reset speed when changing direction
        _playStartedAt: performance.now(),
        _startTime: state.currentTime,
      });
    } else {
      set({ playbackDirection: -1, playbackSpeed: 1 });
      state.play();
    }
  },

  pause: () => {
    const state = get();

    // Cancel the animation loop
    if (state._animationFrameId !== null) {
      cancelAnimationFrame(state._animationFrameId);
    }

    set({
      isPlaying: false,
      playbackSpeed: 1, // Reset speed when pausing
      _playStartedAt: null,
      _animationFrameId: null,
    });
  },

  stop: () => {
    get().pause(); // Stop is the same as pause
  },

  togglePlayback: () => {
    const state = get();
    if (state.isPlaying) {
      state.pause();
    } else {
      // Always play forward when using spacebar
      set({ playbackDirection: 1 });
      state.play();
    }
  },

  seekTo: (time) => {
    const state = get();

    // Clamp time to valid range
    const clampedTime = Math.max(0, Math.min(time, state.totalDuration));

    // If we're playing, we need to reset the start reference
    if (state.isPlaying) {
      set({
        currentTime: clampedTime,
        _playStartedAt: performance.now(),
        _startTime: clampedTime,
      });
    } else {
      set({ currentTime: clampedTime });
    }
  },

  goToStart: () => {
    const { pause, seekTo } = get();
    pause();
    seekTo(0);
  },

  goToEnd: () => {
    const state = get();
    state.pause();
    state.seekTo(state.totalDuration);
  },

  setTotalDuration: (duration) => {
    set((state) => ({
      totalDuration: duration,
      // Clamp currentTime if it exceeds new duration
      currentTime: Math.min(state.currentTime, duration),
    }));
  },

  setFocusedNodeId: (nodeId) => {
    set({ focusedNodeId: nodeId });
  },

  increaseSpeed: () => {
    set((state) => {
      // Cycle through speeds: 1x -> 2x -> 3x -> 4x -> 1x
      const newSpeed = state.playbackSpeed >= 4 ? 1 : state.playbackSpeed + 1;
      
      // If playing, need to reset timing references to account for speed change
      if (state.isPlaying) {
        return {
          playbackSpeed: newSpeed,
          _playStartedAt: performance.now(),
          _startTime: state.currentTime,
        };
      }
      
      return { playbackSpeed: newSpeed };
    });
  },

  resetSpeed: () => {
    set((state) => {
      // Reset to normal speed
      if (state.isPlaying && state.playbackSpeed !== 1) {
        return {
          playbackSpeed: 1,
          _playStartedAt: performance.now(),
          _startTime: state.currentTime,
        };
      }
      
      return { playbackSpeed: 1 };
    });
  },

  setMasterVolume: (volume) => {
    set({ masterVolume: Math.max(0, Math.min(1, volume)) });
  },

  /**
   * Internal tick function - updates currentTime based on elapsed real time
   *
   * This is called every animation frame (~60 times per second).
   * We calculate elapsed time from when playback started to ensure
   * smooth, accurate timing regardless of frame rate.
   */
  _tick: () => {
    const state = get();

    if (!state._playStartedAt) return;

    // Calculate elapsed time since play started
    const now = performance.now();
    const elapsedMs = now - state._playStartedAt;
    const elapsedSeconds = elapsedMs / 1000;

    // Calculate new current time (applying direction and speed)
    let newTime = state._startTime + (elapsedSeconds * state.playbackDirection * state.playbackSpeed);

    // Check boundaries based on direction
    if (state.playbackDirection === 1) {
      // Forward playback
      if (newTime >= state.totalDuration) {
        // Stop at the end
        newTime = state.totalDuration;
        set({ currentTime: newTime });
        state.pause();
        return;
      }
    } else {
      // Reverse playback
      if (newTime <= 0) {
        // Stop at the beginning
        newTime = 0;
        set({ currentTime: newTime });
        state.pause();
        return;
      }
    }

    set({ currentTime: newTime });
  },

  cleanup: () => {
    const state = get();
    if (state._animationFrameId !== null) {
      cancelAnimationFrame(state._animationFrameId);
    }
    set({
      isPlaying: false,
      _animationFrameId: null,
      _playStartedAt: null,
    });
  },
}));

// =============================================================================
// SELECTOR HOOKS
// =============================================================================

/** Get just the playing state */
export const useIsPlaying = () => usePlaybackStore((state) => state.isPlaying);

/** Get just the current time */
export const useCurrentTime = () => usePlaybackStore((state) => state.currentTime);

/** Get total duration */
export const useTotalDuration = () => usePlaybackStore((state) => state.totalDuration);

/** Get focused node ID */
export const useFocusedNodeId = () => usePlaybackStore((state) => state.focusedNodeId);

/** Get playback speed */
export const usePlaybackSpeed = () => usePlaybackStore((state) => state.playbackSpeed);
